import React, { useState, useEffect } from 'react';
import { getServices, createService, updateService, deleteService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Briefcase, 
  Plus, 
  Search,
  Edit2,
  Trash2,
  Euro,
  Clock,
  Upload
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    hourly_rate: '',
    unit: 'hora'
  });

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const { data } = await getServices();
      setServices(data);
    } catch (e) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate)
      };
      
      if (editingService) {
        await updateService(editingService.id, data);
        toast.success('Servicio actualizado');
      } else {
        await createService(data);
        toast.success('Servicio creado');
      }
      loadServices();
      closeModal();
    } catch (e) {
      toast.error('Error al guardar servicio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este servicio?')) return;
    try {
      await deleteService(id);
      toast.success('Servicio eliminado');
      loadServices();
    } catch (e) {
      toast.error('Error al eliminar servicio');
    }
  };

  const openModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || '',
        description: service.description || '',
        hourly_rate: service.hourly_rate?.toString() || '',
        unit: service.unit || 'hora'
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        hourly_rate: '',
        unit: 'hora'
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unitLabels = {
    hora: 'hora',
    unidad: 'ud.',
    proyecto: 'proyecto'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Servicios</h1>
            <p className="text-stone-500 text-sm mt-1">Define tus servicios y tarifas</p>
          </div>
          <PageHelpButton moduleKey="services" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Link to="/import?entity=services">
            <Button variant="outline" className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto" data-testid="import-services-button">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
          </Link>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto"
            data-testid="add-service-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Servicio
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder="Buscar servicios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-xl"
          data-testid="search-services-input"
        />
      </div>

      {/* Services Grid */}
      {filteredServices.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service, index) => (
            <div 
              key={service.id} 
              className={`bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 card-hover animate-fade-in-up stagger-${(index % 5) + 1}`}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-[#FF6B4A]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">{service.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-stone-500">
                      <Clock className="w-3 h-3" />
                      <span>Por {unitLabels[service.unit]}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(service)}
                    className="p-1.5 hover:bg-stone-100 rounded-xl transition-colors"
                    data-testid={`edit-service-${service.id}`}
                  >
                    <Edit2 className="w-4 h-4 text-stone-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="p-1.5 hover:bg-red-50 rounded-xl transition-colors"
                    data-testid={`delete-service-${service.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              {service.description && (
                <p className="text-sm text-stone-600 mb-4 line-clamp-2">
                  {service.description}
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Tarifa</span>
                <span className="text-lg font-mono font-semibold text-[#FF6B4A]">
                  {formatCurrency(service.hourly_rate)}/{unitLabels[service.unit]}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
          <Briefcase className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">No hay servicios</h3>
          <p className="text-stone-500 mb-4">Define tus servicios y tarifas</p>
          <Button
            onClick={() => openModal()}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="add-first-service-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir Servicio
          </Button>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Servicio *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                placeholder="Ej: Consultoría, Desarrollo Web..."
                data-testid="service-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl resize-none"
                rows={3}
                placeholder="Describe el servicio..."
                data-testid="service-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Precio *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    required
                    className="pl-10 rounded-xl font-mono"
                    placeholder="50.00"
                    data-testid="service-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unidad</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="service-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hora">Por hora</SelectItem>
                    <SelectItem value="unidad">Por unidad</SelectItem>
                    <SelectItem value="proyecto">Por proyecto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="save-service-button"
              >
                {editingService ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
